import React, { useState, useEffect } from 'react';
import { 
  FeedbackOption, 
  AppointmentServiceMedia 
} from '../../types';
import { useAppointmentDetails, useStepNavigation } from '../organisms/ReviewWrapper';
import reviewService from '../../services/reviewService';
import apruvRateService from '../../services/apruvRateService';
import AddReviewForm from '../organisms/AddReviewForm';
import PoweredByApruv from '../common/PoweredByApruv';
import checkCircle from '../../assets/images/check-circle.svg';
import { Carousel } from 'primereact/carousel';
import './AddReview.css';
import faceSad from '../../assets/images/face-sad.svg';
import faceSadActive from '../../assets/images/face-sad-active.svg';
import faceNeutral from '../../assets/images/face-neutral.svg';
import faceNeutralActive from '../../assets/images/face-neutral-active.svg';
import faceSmile from '../../assets/images/face-smile.svg';
import faceSmileActive from '../../assets/images/face-smile-active.svg';
import faceHappy from '../../assets/images/face-happy.svg';
import faceHappyActive from '../../assets/images/face-happy-active.svg';

interface AddReviewProps {
  isOldReview?: boolean;
  onStepChange?: (step: string) => void;
}

const SOFT_FEEDBACK_COMPANY_ID = 20;

const AddReview: React.FC<AddReviewProps> = ({ isOldReview = false, onStepChange }) => {
  const appointmentDetails = useAppointmentDetails(); // Get from context instead of state
  const { goToNegative, goToTipping, goToThankYou } = useStepNavigation();
  
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackOption | null>(null);
  const [appointmentNotes, setAppointmentNotes] = useState<string>('');
  const [appointmentImages, setAppointmentImages] = useState<string[]>([]);
  const [appointmentVideos, setAppointmentVideos] = useState<string[]>([]);
  const [appointmentRecordings, setAppointmentRecordings] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'Photo' | 'Video' | 'Voicemail'>('Photo');
  const [loading, setLoading] = useState(false);
  const [rateConfig, setRateConfig] = useState(apruvRateService.getRateConfig());

  // Fetch rate config if not already loaded
  useEffect(() => {
    const loadConfig = async () => {
      // First check if the service already has the config (from ReviewWrapper)
      const existingConfig = apruvRateService.getRateConfig();
      if (existingConfig) {
        setRateConfig(existingConfig);
        return;
      }

      // If not available and we have appointment details, fetch it
      if (appointmentDetails?.appointment_details) {
        try {
          const config = await apruvRateService.getConfig(
            appointmentDetails.appointment_details.CompanyID,
            appointmentDetails.appointment_details.LocationID
          );
          setRateConfig(config);
        } catch (error) {
          console.error('Error fetching rate config:', error);
        }
      }
    };

    loadConfig();
  }, [appointmentDetails]);

  const isNPS = rateConfig?.ScoreMethod === 'NPS';

  // CSAT feedbacks (1-4 scale with icons)
  const csatFeedbacks: FeedbackOption[] = [
    { name: 'Bad', value: 1, icon: faceSad, iconActive: faceSadActive },
    { name: 'Okay', value: 2, icon: faceNeutral, iconActive: faceNeutralActive },
    { name: 'Good', value: 3, icon: faceSmile, iconActive: faceSmileActive },
    { name: 'Great', value: 4, icon: faceHappy, iconActive: faceHappyActive },
  ];

  // NPS feedbacks (0-10 scale)
  const npsFeedbacks: FeedbackOption[] = Array.from({ length: 11 }, (_, i) => ({
    name: String(i),
    value: i,
    icon: i <= 6 ? faceSad : i <= 8 ? faceNeutral : faceHappy,
    iconActive: i <= 6 ? faceSadActive : i <= 8 ? faceNeutralActive : faceHappyActive,
  }));

  const feedbacks = isNPS ? npsFeedbacks : csatFeedbacks;

  // Process service media when appointment details are available
  useEffect(() => {
    if (!appointmentDetails?.appointment_details?.ServiceMedia) return;

    const images: string[] = [];
    const videos: string[] = [];
    const recordings: string[] = [];
    let notes = '';

    appointmentDetails.appointment_details.ServiceMedia.forEach((media: AppointmentServiceMedia) => {
      switch (media.MediaType) {
        case 'image':
          images.push(media.MediaURL);
          break;
        case 'video':
          videos.push(media.MediaURL);
          break;
        case 'voice_recording':
          recordings.push(media.MediaURL);
          break;
        case 'note':
          notes = media.Notes || '';
          break;
      }
    });

    setAppointmentImages(images);
    setAppointmentVideos(videos);
    setAppointmentRecordings(recordings);
    setAppointmentNotes(notes);

    // Set default active tab based on available media
    if (images.length > 0) setActiveTab('Photo');
    else if (videos.length > 0) setActiveTab('Video');
    else if (recordings.length > 0) setActiveTab('Voicemail');
  }, [appointmentDetails]);

  const handleSubmit = async () => {
    if (!selectedFeedback || !appointmentDetails) return;

    try {
      setLoading(true);

      // Determine if this is a negative/feedback review
      // CSAT: negative is < 3 by default, but company 20 treats 3 as feedback too
      // NPS: detractors (0-6) and passives (7-8) go to feedback page
      const usesSoftFeedbackFlow = appointmentDetails.appointment_details.CompanyID === SOFT_FEEDBACK_COMPANY_ID;
      const shouldShowFeedback = isNPS
        ? selectedFeedback.value <= 8  // NPS: detractors (0-6) and passives (7-8)
        : usesSoftFeedbackFlow
          ? selectedFeedback.value <= 3
          : selectedFeedback.value < 3;

      if (shouldShowFeedback) {
        // Store the feedback ID for negative review
        reviewService.setFedId(selectedFeedback.value);

        // Navigate to negative review page
        if (isOldReview && onStepChange) {
          onStepChange('negative');
        } else {
          goToNegative();
        }
      } else {
        // Submit positive review
        await reviewService.submitReview({
          ID: appointmentDetails.appointment_details.AppointmentId,
          Rating: selectedFeedback.value
        });

        const shouldShowTipping = rateConfig?.EnableTipping &&
          appointmentDetails.technician_details.connected_account &&
          !appointmentDetails.appointment_details.IsInitial;

        if (shouldShowTipping) {
          if (isOldReview && onStepChange) {
            onStepChange('tipping');
          } else {
            goToTipping();
          }
        } else {
          if (isOldReview && onStepChange) {
            onStepChange('thank-you');
          } else {
            goToThankYou();
          }
        }
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTip = () => {
    if (isOldReview && onStepChange) {
      onStepChange('tipping');
    } else {
      goToTipping();
    }
  };

  const handleRefuseTip = () => {
    if (isOldReview && onStepChange) {
      onStepChange('thank-you');
    } else {
      goToThankYou();
    }
  };

  // Get the correct rating header and company message from rateConfig (using state variable)
  const ratingHeader = rateConfig?.Header || '';
  const companyMessage = rateConfig?.CompanyMessageTemplates?.find(m => m.ID === rateConfig?.ID)?.Message || '';

  if (loading) {
    return (
      <div className="add-review-container">
        <div className="add-review-content" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div>Loading...</div>
          <PoweredByApruv />
        </div>
      </div>
    );
  }

  if (appointmentDetails?.appointment_details?.IsRated) {
    // Already rated: show thank you or tip prompt (match Figma design)
    const mediaTabs: ('Photo' | 'Video' | 'Voicemail')[] = [];
    if (appointmentImages.length > 0) mediaTabs.push('Photo');
    if (appointmentVideos.length > 0) mediaTabs.push('Video');
    if (appointmentRecordings.length > 0) mediaTabs.push('Voicemail');

    return (
      <div className="figma-container">
        <div className="figma-content">
          
          {mediaTabs.length > 0 && (
            <div className="figma-tabs">
              {mediaTabs.map((tab, index) => (
                <div
                  key={tab}
                  className={`figma-tab ${activeTab === tab ? 'figma-tab-active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  <span className="figma-tab-label">{tab}</span>
                </div>
              ))}
            </div>
          )}

          {/* Media Content */}
          {mediaTabs.length > 0 && (
            <div className="figma-carousel-container">
              {activeTab === 'Photo' && appointmentImages.length > 0 && (
                <Carousel
                  value={appointmentImages}
                  numVisible={1}
                  numScroll={1}
                  circular
                  showNavigators={false}
                  showIndicators={appointmentImages.length > 1}
                  className="figma-carousel"
                  itemTemplate={(image: string) => (
                    <div className="figma-image-wrapper">
                      <img
                        src={image}
                        alt="Service"
                        className="figma-carousel-image"
                      />
                    </div>
                  )}
                />
              )}
              {activeTab === 'Video' && appointmentVideos.length > 0 && (
                <Carousel
                  value={appointmentVideos}
                  numVisible={1}
                  numScroll={1}
                  circular
                  showNavigators={false}
                  showIndicators={appointmentVideos.length > 1}
                  className="figma-carousel"
                  itemTemplate={(video: string) => (
                    <div className="figma-image-wrapper">
                      <video
                        src={video}
                        controls
                        className="figma-carousel-image"
                      />
                    </div>
                  )}
                />
              )}
              {activeTab === 'Voicemail' && appointmentRecordings.length > 0 && (
                <Carousel
                  value={appointmentRecordings}
                  numVisible={1}
                  numScroll={1}
                  circular
                  showNavigators={false}
                  showIndicators={appointmentRecordings.length > 1}
                  className="figma-carousel"
                  itemTemplate={(audio: string) => (
                    <div className="figma-image-wrapper">
                      <audio
                        src={audio}
                        controls
                        className="figma-carousel-audio"
                      />
                    </div>
                  )}
                />
              )}
            </div>
          )}

          {/* Notes Section */}
          {appointmentNotes && (
            <div className="figma-notes-section">
              <div className="figma-notes-label">Notes</div>
              <div className="figma-notes-content">
                {appointmentNotes}
              </div>
            </div>
          )}

          {/* Feedback Card */}
          {mediaTabs.length === 0 && (
            <div className="figma-company-image">
              <img src={appointmentDetails?.appointment_details?.CompanyImageUrl} alt="" />
            </div>
          )}
          <div className="figma-feedback-card">
            <img className="figma-check-icon" src={checkCircle} alt="Checked" />
            <div className="figma-feedback-text">
              <h2 className="figma-feedback-title">
                You have already submitted feedback for {appointmentDetails?.appointment_details?.TechnicianFirstName}
              </h2>
              {appointmentDetails?.technician_details?.connected_account && (
                <p className="figma-feedback-subtitle">
                  Would you like to send a tip?
                </p>
              )}
            </div>
          </div>

          {/* Tip Buttons */}
          {appointmentDetails?.technician_details?.connected_account && (
            <div className="figma-button-group">
              <button onClick={handleRefuseTip} className="figma-button figma-button-primary">
                No
              </button>
              <button onClick={handleSendTip} className="figma-button figma-button-outlined">
                Yes
              </button>
            </div>
          )}

          <PoweredByApruv sx={{ mt: 'auto' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="add-review-container">
      <div className="add-review-content">
        {/* Header Section */}
        <div className="add-review-header">
          <div className="add-review-header-text">
            <h1 className="add-review-title">
              {ratingHeader || "You've been invited to review Pest Control Company"}
            </h1>
          </div>
        </div>

        {/* Company Image - only show if no media */}
        {!(appointmentImages.length > 0 || appointmentVideos.length > 0 || appointmentRecordings.length > 0) && (
          <div className="figma-company-image mt-0">
            <img src={appointmentDetails?.appointment_details?.CompanyImageUrl} alt="" />
          </div>
        )}

        {/* Tabs and Media Section */}
        {(appointmentImages.length > 0 || appointmentVideos.length > 0 || appointmentRecordings.length > 0) && (
          <div className="add-review-media-section">
            {/* Tabs */}
            <div className="add-review-tabs">
              {appointmentImages.length > 0 && (
                <div
                  className={`add-review-tab ${activeTab === 'Photo' ? 'add-review-tab-active' : ''}`}
                  onClick={() => setActiveTab('Photo')}
                >
                  <span className="add-review-tab-label">Photo</span>
                </div>
              )}
              {appointmentVideos.length > 0 && (
                <div
                  className={`add-review-tab ${activeTab === 'Video' ? 'add-review-tab-active' : ''}`}
                  onClick={() => setActiveTab('Video')}
                >
                  <span className="add-review-tab-label">Video</span>
                </div>
              )}
              {appointmentRecordings.length > 0 && (
                <div
                  className={`add-review-tab ${activeTab === 'Voicemail' ? 'add-review-tab-active' : ''}`}
                  onClick={() => setActiveTab('Voicemail')}
                >
                  <span className="add-review-tab-label">Voicemail</span>
                </div>
              )}
            </div>

            {/* Media Content */}
            <div className="add-review-carousel-container">
              {activeTab === 'Photo' && appointmentImages.length > 0 && (
                <Carousel
                  value={appointmentImages}
                  numVisible={1}
                  numScroll={1}
                  circular
                  showNavigators={false}
                  showIndicators={appointmentImages.length > 1}
                  className="add-review-carousel"
                  itemTemplate={(image: string) => (
                    <div className="add-review-image-wrapper">
                      <img
                        src={image}
                        alt="Service"
                        className="add-review-carousel-image"
                      />
                    </div>
                  )}
                />
              )}
              {activeTab === 'Video' && appointmentVideos.length > 0 && (
                <Carousel
                  value={appointmentVideos}
                  numVisible={1}
                  numScroll={1}
                  circular
                  showNavigators={false}
                  showIndicators={appointmentVideos.length > 1}
                  className="add-review-carousel"
                  itemTemplate={(video: string) => (
                    <div className="add-review-image-wrapper">
                      <video
                        src={video}
                        controls
                        className="add-review-carousel-image"
                      />
                    </div>
                  )}
                />
              )}
              {activeTab === 'Voicemail' && appointmentRecordings.length > 0 && (
                <Carousel
                  value={appointmentRecordings}
                  numVisible={1}
                  numScroll={1}
                  circular
                  showNavigators={false}
                  showIndicators={appointmentRecordings.length > 1}
                  className="add-review-carousel"
                  itemTemplate={(audio: string) => (
                    <div className="add-review-image-wrapper">
                      <audio
                        src={audio}
                        controls
                        className="add-review-carousel-audio"
                      />
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {appointmentNotes && (
          <div className="add-review-notes-section">
            <div className="add-review-notes-label">Notes</div>
            <div className="add-review-notes-content">
              {appointmentNotes}
            </div>
          </div>
        )}

        {/* Rating Form */}
        <div>
          <label>{rateConfig?.RatingHeader}</label>
          <AddReviewForm
            feedbacks={feedbacks}
            selectedFeedback={selectedFeedback}
            onSelectFeedback={setSelectedFeedback}
            appointmentImages={appointmentImages}
            appointmentVideos={appointmentVideos}
            appointmentRecordings={appointmentRecordings}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            appointmentNotes={appointmentNotes}
            ratingHeader={ratingHeader}
            companyMessage={companyMessage}
            loading={loading}
            onSubmit={handleSubmit}
            onSendTip={handleSendTip}
            onRefuseTip={handleRefuseTip}
          />
        </div>

        <PoweredByApruv sx={{ mt: 'auto' }} />
      </div>
    </div>
  );
};

export default AddReview;
